export type LabelMap = Record<string, string | undefined> & { en: string }

export interface Progress {
  completedSteps: number
  totalSteps: number
}

export interface UploadedDocument {
  documentId: string
  documentRequestId: string
  fileName: string
}

export interface ShowIf {
  questionId: string
  equals: ScalarAnswerValue
}

/** Constant operand of a screen-level `sumComparison`. */
export interface ConstantOperand {
  kind: 'constant'
  value: number
}

export type ConditionOperand = SumOperand | ConstantOperand

/** Sum of `left` compared with the sum of `right`; unanswered operands count as 0. */
export interface SumComparisonCondition {
  kind: 'sumComparison'
  left: ConditionOperand[]
  operator: 'gt' | 'gte' | 'lt' | 'lte'
  right: ConditionOperand[]
}

export interface AnsweredCondition {
  kind: 'answered'
  questionId: string
}

export type ScreenConditionLeaf =
  | (ShowIf & { kind?: undefined })
  | SumComparisonCondition
  | AnsweredCondition

/**
 * Screen-level visibility (screens and the completion). Evaluated by the backend only — the
 * frontend never decides which screen comes next — so it may compute, unlike a question's
 * `ShowIf`.
 */
export type ScreenCondition =
  | ScreenConditionLeaf
  | { kind: 'all'; conditions: ScreenConditionLeaf[] }

/**
 * Hard cross-field rule: this question's numeric value must be strictly less than the
 * referenced question's (a select's option value is read as a number). Skipped while either
 * side is unanswered or non-numeric. Top-level questions only.
 */
export interface LessThan {
  questionId: string
}

/** Answer reference operand of a `sum`. */
export interface AnswerOperand {
  kind?: undefined
  questionId: string
  /** When set, sums this inner number across every item of the repeatable group `questionId`. */
  innerQuestionId?: string
}

/** Value looked up from a source answer, e.g. the living-cost matrix keyed by household size. */
export interface LookupDefault {
  kind: 'lookup'
  questionId: string
  /** Keyed by the source answer stringified (select option values as-is). */
  mapping: Record<string, number>
  fallback?: number
}

/** A `sum` operand: an answer reference, or an inline lookup evaluated like the `lookup` kind. */
export type SumOperand = AnswerOperand | LookupDefault

/**
 * Auto-calculated suggestion for a number question: the field is filled with the computed
 * value whenever a source answer changes and stays editable. Takes precedence over
 * `defaultValue` once evaluable.
 */
export type DefaultFrom = LookupDefault | { kind: 'sum'; operands: SumOperand[] }

interface BaseQuestion {
  id: string
  label: LabelMap
  placeholder?: LabelMap
  required?: boolean
  helpText?: LabelMap
  showIf?: ShowIf
  unit?: string
  unitLabel?: LabelMap
  prefill?: unknown
}

export interface NumberQuestion extends BaseQuestion {
  type: 'number'
  min?: number
  max?: number
  defaultValue?: number
  defaultFrom?: DefaultFrom
  applyDefaultLabel?: LabelMap
  lessThan?: LessThan
  dataType?: 'integer' | 'decimal'
}

export type TextQuestionFormat = 'iban'

export interface TextQuestion extends BaseQuestion {
  type: 'text'
  maxLength?: number
  format?: TextQuestionFormat
}

export interface BooleanQuestion extends BaseQuestion {
  type: 'boolean'
}

export interface SelectOptionConfig {
  value: string
  label: LabelMap
  icon?: string
  badge?: LabelMap
  descriptionItems?: LabelMap[]
  /**
   * Tile selects: a heading rendered above this tile that opens a group of tiles, up to the next
   * option with a heading (e.g. „Einkommensnachweis" over the income-proof tiles, „Vermögensnachweis"
   * over the asset-proofs tile). The select stays one exclusive choice.
   */
  heading?: LabelMap
  /** For `buttons` selects: which button is the highlighted action. Default secondary. */
  emphasis?: 'primary' | 'secondary'
}

export interface SelectQuestion extends BaseQuestion {
  type: 'select'
  /**
   * `buttons`: one action button per option in place of „Weiter" — clicking submits the screen.
   * The backend guarantees such a select is the only question of its screen.
   */
  display?: 'dropdown' | 'tiles' | 'buttons'
  lessThan?: LessThan
  /** Always concrete: dynamic (`optionsFrom`) selects are resolved server-side, possibly to `[]`. */
  options: SelectOptionConfig[]
}

export type ScalarQuestion = NumberQuestion | TextQuestion | BooleanQuestion | SelectQuestion

export interface GroupQuestion {
  type: 'group'
  id: string
  label: LabelMap
  helpText?: LabelMap
  showIf?: ShowIf
  addItemLabel?: LabelMap
  minItems?: number
  maxItems?: number
  initialItems?: number
  questions: ScalarQuestion[]
}

export type Question = ScalarQuestion | GroupQuestion

export type ScalarAnswerValue = string | number | boolean
export type GroupItem = Partial<Record<string, ScalarAnswerValue>>
export type AnswerValue = ScalarAnswerValue | GroupItem[]
export type Answers = Partial<Record<string, AnswerValue>>

export interface HelpContent {
  title: LabelMap
  text: LabelMap
}

interface BaseScreen {
  id: string
  title: LabelMap
  description?: LabelMap
  /** Icon rendered above the title (an identifier from the icon set). */
  icon?: string
  help?: HelpContent
  showIf?: ScreenCondition
}

export interface QuestionsScreen extends BaseScreen {
  type: 'questions'
  questions: Question[]
}

export interface InfoSection {
  title?: LabelMap
  icon?: string
  items: LabelMap[]
}

export interface InfoScreen extends BaseScreen {
  type: 'info'
  sections?: InfoSection[]
}

export interface DocumentRequest {
  id: string
  label: LabelMap
  description?: LabelMap
  /** Bullet lines under the slot label, e.g. which documents count as an asset proof. */
  descriptionItems?: LabelMap[]
  /** Default true. */
  required?: boolean
  /** Default 1 (only enforced when the slot is required). */
  minFiles?: number
  /** Default 10. */
  maxFiles?: number
  allowedContentTypes: string[]
  /** Server default applies when absent — see `slotMaxSizeMb`. */
  maxSizeMb?: number
}

export interface FileUploadScreen extends BaseScreen {
  type: 'fileUpload'
  fileUpload: {
    documentRequests: DocumentRequest[]
  }
}

export interface ReviewEntry {
  /** A `questions` screen preceding the review; its definition is in the back stack. */
  screenId: string
  /** Card heading, e.g. the person's name. */
  title?: LabelMap
}

export interface ReviewSection {
  title: LabelMap
  /** One edit button for a single entry; one per entry (card) when there are several. */
  entries: ReviewEntry[]
}

/**
 * The saved answers of earlier screens, in sections with an edit button each. Collects nothing:
 * „Weiter" submits it with `answers: {}`.
 */
export interface ReviewScreen extends BaseScreen {
  type: 'review'
  editLabel: LabelMap
  sections: ReviewSection[]
}

export type Screen = QuestionsScreen | FileUploadScreen | InfoScreen | ReviewScreen

export interface CompleteContent {
  title: LabelMap
  description?: LabelMap
  buttonLabel?: LabelMap
}

export type Step =
  | { kind: 'screen'; screen: Screen }
  | { kind: 'complete'; content?: CompleteContent }

/**
 * Why this data-collection transaction was (re)started. Present only when the backend rewound the
 * user here after a failed attempt; absent on a first run or a plain resumption. Compare by value:
 * the backend enum may grow.
 */
// The backend enum may grow; an unknown value is treated as "no notice".
export const KNOWN_RETRY_REASONS = ['openBankingFailed', 'openBankingCancelled'] as const
export type RetryReason = (typeof KNOWN_RETRY_REASONS)[number]

export interface GetContextResponse {
  step: Step
  progress: Progress
  answers: Answers
  uploadedDocuments?: UploadedDocument[]
  retryReason?: RetryReason
  previousScreens?: Screen[]
}

export interface SubmitAnswersRequest {
  screenId: string
  answers: Answers
  /**
   * The review screen the user came from when editing `screenId`: the backend serves it again
   * (instead of the screen following the edited one) while it still applies.
   */
  returnToScreenId?: string
}

export interface SubmitAnswersResponse {
  step: Step
  progress: Progress
}

export interface UploadDocumentRequest {
  documentRequestId: string
  file: string
  fileName: string
}

export interface UploadDocumentResponse {
  documentId: string
  documentRequestId: string
  fileName: string
  uploadedDocuments: UploadedDocument[]
  step: Step
  progress: Progress
}

// Presigned (large-file) upload — mirrors the zod contracts in attempt-api
// (contracts/data-collection-contracts/src/{requestDocumentUpload,confirmDocumentUpload}.ts).
// The browser uploads straight to S3, bypassing the API Gateway base64 size cap.

export interface RequestDocumentUploadRequest {
  documentRequestId: string
  contentType: string
}

export interface RequestDocumentUploadResponse {
  documentId: string
  /** S3 endpoint to POST the multipart form to. */
  url: string
  /** Opaque policy/signature fields — appended to the form verbatim, before the file. */
  fields: Record<string, string>
}

export interface ConfirmDocumentUploadRequest {
  documentRequestId: string
  documentId: string
  fileName: string
}

// The confirm response is the same shape as /upload-document, so the rest of
// the flow (step/progress/uploadedDocuments rendering) is identical.
export type ConfirmDocumentUploadResponse = UploadDocumentResponse

export interface DeleteDocumentRequest {
  documentId: string
}

export interface DeleteDocumentResponse {
  uploadedDocuments: UploadedDocument[]
  step: Step
  progress: Progress
}

export interface DataCollectionErrorResponse {
  error: string
  message: string
}

export const DATA_COLLECTION_ERROR_CODES = [
  'DataCollection.InvalidAnswers',
  'DataCollection.TransactionAlreadyCompleted',
  'DataCollection.InvalidDataUri',
  'DataCollection.UnsupportedFileType',
  'DataCollection.FileTooLarge',
  'DataCollection.InvalidPdfFile',
  'DataCollection.InvalidImageFile',
  'DataCollection.ImageTooManyPixels',
  'DataCollection.TooManyDocuments',
  'DataCollection.UploadLimitReached',
  'DataCollection.DocumentUploadNotEnabled',
  'DataCollection.UnknownDocumentRequest',
  'DataCollection.DocumentNotFound',
  'DataCollection.DocumentNotUploaded',
  'DataCollection.TransactionNotEditable',
] as const

export type DataCollectionErrorCode = (typeof DATA_COLLECTION_ERROR_CODES)[number]
